import { useEffect, useState } from 'react';

import { Header } from '@tanstack/react-table';

interface UseColumnResizeReturn {
  isVisible: boolean;
  isResizing: boolean;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  resizeHandler: (event: unknown) => void;
}

/**
 * Hook that manages column resize handle visibility and state.
 *
 * Controls when the resize handle is visible (on hover or during resize)
 * and provides the necessary event handlers for the resize interaction.
 *
 * @template TData - The type of data in the table.
 * @param header - The header instance from react-table.
 * @returns Object containing visibility state and event handlers.
 *
 * @example
 * ```tsx
 * const { isVisible, handleMouseEnter, handleMouseLeave, resizeHandler } =
 *   useColumnResize(header);
 *
 * return (
 *   <div
 *     onMouseDown={resizeHandler}
 *     onMouseEnter={handleMouseEnter}
 *     onMouseLeave={handleMouseLeave}
 *     style={{ opacity: isVisible ? 1 : 0 }}
 *   />
 * );
 * ```
 */
export const useColumnResize = <TData,>(
  header: Header<TData, unknown>
): UseColumnResizeReturn => {
  const [isHovered, setIsHovered] = useState(false);
  const isResizing = header.column.getIsResizing();

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (!isResizing) {
      setIsHovered(false);
    }
  };

  useEffect(() => {
    if (!isResizing) {
      setIsHovered(false);
    }
  }, [isResizing]);

  const isVisible = isHovered || isResizing;

  return {
    isVisible,
    isResizing,
    handleMouseEnter,
    handleMouseLeave,
    resizeHandler: header.getResizeHandler()
  };
};
